import type { FunctionalComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { env } from '@/lib/env.ts';
import { useGoogleSdk } from '@/hooks/use-external-script.ts';
import { appRequest, isApp, isAppCancel } from '@/lib/app-bridge.ts';
import { getInflow } from '@/lib/inflow.ts';
import { exchangeOauth } from '@/lib/oauth.ts';
import type { DaoUser } from '@/types/dao.ts';

interface Props {
  onComplete?: (user: DaoUser) => void;
  onFail?: (message: string) => void;
}

/**
 * 구글 로그인 버튼.
 *
 * GIS 는 자기가 그린 버튼만 클릭을 받는다. 시안 디자인을 쓰기 위해 GIS 버튼을
 * 숨겨두고 클릭을 넘긴다. 숨긴 버튼을 못 찾으면 One Tap(prompt)으로 넘어간다.
 *
 * 구글은 팝업이 아니라 콜백으로 id_token 을 바로 주므로, 여기서 백엔드에
 * 검증을 요청하고 결과를 onComplete 로 올린다.
 *
 * ★ **앱 안에서는 GIS 를 쓰지 않는다.** (2026-10-08)
 *   구글은 웹뷰 안의 로그인을 막는다(`403 disallowed_useragent`). 앱이 안드로이드 기본
 *   구글 로그인으로 id_token 을 받아 넘겨 준다(`app-bridge.ts`). 토큰의 aud 가 같은
 *   웹 클라이언트 ID 라서 백엔드 검증은 그대로다.
 */
export const GoogleLoginButton: FunctionalComponent<Props> = ({ onComplete, onFail }) => {
  const status = useGoogleSdk();
  const hostRef = useRef<HTMLDivElement>(null);

  // 콜백은 GIS 초기화 시점에 한 번만 등록되므로, 최신 핸들러를 ref 로 참조한다.
  const handlers = useRef({ onComplete, onFail });
  handlers.current = { onComplete, onFail };

  useEffect(() => {
    if (status !== 'ready') return;
    if (!window.google?.accounts?.id || !hostRef.current) return;
    if (!env.google.clientId) return;

    window.google.accounts.id.initialize({
      client_id: env.google.clientId,
      callback: ({ credential }) => void verify(credential),
    });

    window.google.accounts.id.renderButton(hostRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
    });
  }, [status]);

  async function verify(credential: string) {
    try {
      // inflow — 광고·제휴 유입 경로. 가입할 때 한 번만 회원 정보에 남는다.
      const user = await exchangeOauth(env.google.api, {
        id_token: credential,
        inflow: getInflow(),
      });
      handlers.current.onComplete?.(user);
    } catch (err) {
      handlers.current.onFail?.(err instanceof Error ? err.message : '구글 로그인에 실패했습니다.');
    }
  }

  async function loginInApp() {
    let idToken: string;
    try {
      ({ idToken } = await appRequest<{ idToken: string }>('googleLogin'));
    } catch (err) {
      if (isAppCancel(err)) return;
      onFail?.(err instanceof Error ? err.message : '구글 로그인에 실패했습니다.');
      return;
    }
    await verify(idToken);
  }

  function handleClick() {
    if (!env.google.clientId || !env.google.api) {
      onFail?.('구글 로그인이 설정되지 않았습니다.');
      return;
    }

    if (isApp()) {
      void loginInApp();
      return;
    }

    if (status === 'error') {
      onFail?.('구글 로그인 모듈을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    const trigger = hostRef.current?.querySelector<HTMLElement>('div[role=button], button');
    if (trigger) {
      trigger.click();
      return;
    }

    // GIS 버튼 DOM 구조가 바뀌었거나 아직 안 그려졌을 때의 대비책.
    window.google?.accounts?.id?.prompt();
  }

  return (
    <>
      <button type="button" class="login-btn login-btn--google" onClick={handleClick}>
        <span class="login-btn-icon">
          <img src="/images/google.png" alt="" />
        </span>
        구글 로그인
      </button>

      {/* GIS 가 실제 버튼을 그리는 자리. 화면에는 보이지 않는다. */}
      <div ref={hostRef} hidden />
    </>
  );
};
